import React, { useCallback, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { Redirect } from 'react-router'
import Spinner from '../../../components/Spinner'
import JobNavigation from '../../components/JobNavigation'
import link from '../../../infra/link'
import { Id } from '../../../types/definitions'
import styles from './JobCheckoutSection.sass'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/Card'
import Button from '../../../components/Button'
import CallCenterJobViewModel from '../JobSection/CallCenterJobViewModel'
import AddCouponDialog from '../AddCouponDialog'
import { useActivation } from '../../../hooks/mobx-hooks'
import Icon from '../../../components/Icon'
import JobCheckoutViewModel, {
  AppliedCouponViewModel,
} from '../../../jobs/views/JobRoot/Checkout/JobCheckoutViewModel'
import { Task } from 'mobx-task'
import { formatMoney } from '../../../utils/money-util'
import { FormGroup, FormRow } from 'components/inputs'
import TextInput from 'components/inputs/TextInput'

interface JobPricingSectionProps {
  jobVm: CallCenterJobViewModel
}

export default observer(function JobCheckout({
  jobVm,
}: JobPricingSectionProps) {
  const vm = jobVm.jobCheckoutViewModel

  useEffect(() => {
    if (vm.job.id && !vm.fetchJobInfo.pending) {
      void vm.fetchSummary()
      void vm.refreshJob()
    }
  }, [vm])

  if (vm.fetchJobInfo.pending || !vm.job.id) {
    return (
      <div>
        <Spinner size={20} />
      </div>
    )
  }

  if (!jobVm.isCheckoutReady) {
    return <Redirect push={false} to={link.callCenterJob(Id.assert(vm.id))} />
  }

  return (
    <div className={styles.root}>
      <Card unconstrained>
        <CardHeader>
          <CardTitle>$ Pricing</CardTitle>
        </CardHeader>

        <CardContent scroll>
          <div className={styles.content}>
            <div className={styles.pricing}>
              <div className={styles.total}>{formatMoney(vm.amountDueNow)}</div>
              <div>Amount due now</div>
            </div>

            <PriceBreakdown jobVm={jobVm} checkoutVm={vm} />

            <div className={styles.checkoutButtons}>
              {!vm.hasCoupons && (
                <Button
                  onClick={() => jobVm.addCouponViewModel.show()}
                  size="cta"
                  type="primary"
                >
                  Add Coupon
                </Button>
              )}
            </div>

            <CheckoutLink
              checkoutLink={jobVm.jobCheckoutViewModel.checkoutLink}
              isEmailingCheckoutLink={jobVm.isEmailingCheckoutLink}
              sendEmail={jobVm.emailCheckoutLink}
            />
          </div>
        </CardContent>
      </Card>

      <JobNavigation onPrevious={() => jobVm.goBackToJob()} />

      <AddCouponDialog vm={jobVm.addCouponViewModel} />
    </div>
  )
})

interface PriceBreakdownProps {
  jobVm: CallCenterJobViewModel
  checkoutVm: JobCheckoutViewModel
}

const PriceBreakdown = observer(function PriceBreakDown({
  checkoutVm,
  jobVm,
}: PriceBreakdownProps) {
  return (
    <div className={styles.breakdown}>
      <b>Services</b>
      <div className={styles.items}>
        {jobVm.jobScopeItems.map((item) => (
          <div key={item.id} className={styles.item}>
            <div>{item.name}</div>
          </div>
        ))}
      </div>

      {checkoutVm.hasCoupons && (
        <>
          <div className={styles.item}>
            <div>Discount coupon</div>
            <div>{checkoutVm.discountLabel}</div>
          </div>

          {checkoutVm.appliedCouponVM && (
            <CouponDetail
              vm={checkoutVm.appliedCouponVM}
              remove={jobVm.removeCoupon}
            />
          )}
        </>
      )}

      <b>Price Breakdown</b>
      <div className={styles.item}>
        <div>Primary Amount</div>
        <div>{checkoutVm.primaryAmount}</div>
      </div>
      <div className={styles.item}>
        <div>Coupon</div>
        <div>{checkoutVm.discount}</div>
      </div>
      <div className={styles.item}>
        <div>Total</div>
        <div>{checkoutVm.total}</div>
      </div>
    </div>
  )
})

interface CouponDetailProps {
  vm: AppliedCouponViewModel
  remove: Task<[], Promise<void>>
}

const CouponDetail = observer(function CouponDetail({
  vm,
  remove,
}: CouponDetailProps) {
  useActivation(vm)

  return (
    <div className={styles.couponSummary}>
      <div className={styles.appliedBadge}>
        <Icon size={12} name="tick" /> Applied
      </div>
      <div>
        <div>{vm.name}</div>
        <div>{vm.description}</div>
      </div>
      <div>{vm.discount}</div>
      <Button
        loading={remove.pending}
        type="danger"
        borderless
        onClick={remove}
      >
        <Icon size={24} name="close" />
      </Button>
    </div>
  )
})

interface GeneratedCheckoutLinkProps {
  checkoutLink: string
  isEmailingCheckoutLink: boolean
  sendEmail: () => Promise<void>
}

function CheckoutLink({
  checkoutLink,
  isEmailingCheckoutLink,
  sendEmail,
}: GeneratedCheckoutLinkProps) {
  const [copied, setCopied] = useState(false)

  const copyUrl = useCallback(async () => {
    await navigator.clipboard.writeText(checkoutLink)
    setCopied(true)
  }, [checkoutLink])

  useEffect(() => {
    let timerId = null

    if (copied) {
      timerId = setTimeout(() => setCopied(false), 2000)
    }

    return () => {
      if (timerId) {
        clearTimeout(timerId)
      }
    }
  }, [copied])

  return (
    <FormRow>
      <FormGroup label="Checkout Link">
        <TextInput
          disabled
          value={checkoutLink}
          button={
            <div className={styles.linkButtons}>
              <Button
                onClick={copyUrl}
                type={copied ? 'success' : 'default'}
                iconLeft={<Icon auto name="copy" />}
              >
                {copied ? 'Copied!' : 'Copy'}
              </Button>

              <Button
                loading={isEmailingCheckoutLink}
                onClick={sendEmail}
                iconRight={<Icon auto name="arrow" />}
              >
                Send Email
              </Button>
            </div>
          }
        />
      </FormGroup>
    </FormRow>
  )
}
